import React from 'react';
import { Provider } from 'react-redux';
import store from '../../redux/store.js';

import { FormContext, getDefaultContext, loadDatagridContext } from './form_context.jsx';
import FormContent, { NO_ANSWERS } from './form_content.jsx';

import * as FormComponent from './form_components.jsx';

import {
  ViewMode,
  Mode,
  CONTEXT_OPTIONS,
  ValidationType
} from './common.jsx';
import Helpers from './components/helpers.jsx';

import Resolver from './component_resolver.jsx';
import ToggleSwitch from '../common/ToggleSwitch.jsx';
import LoadingIndicator from '../common/loading_indicator.jsx';

import { update } from '../../helpers/index.js';
import FormViewerSkeleton from '../../skeleton_loaders/FormViewerSkeleton.jsx';

import * as apiMethods from './form_viewer_api.js'
import { resolveDatagridDerivedAnswers } from './datagrid_dependency_resolver.js'
import PaginationContext, { PaginationBoundary } from './pagination_context.jsx';

/*
* Root of the viewer app. Holds the state.
*/
export default class FormViewer extends React.Component {
        constructor(props) {
            super(props);

            this.state = {
                form: typeof props.form == 'object' ? props.form : JSON.parse(props.form || '{}'),
                formId: props.formId,
                answers: typeof props.answers == 'object' ? props.answers : JSON.parse(props.answers || '{}'),
                attachments: typeof props.attachments == 'object' ? props.attachments : JSON.parse(props.attachments || '{}'),
                comments: typeof props.comments == 'object' ? props.comments : JSON.parse(props.comments || '{}'),
                mode: this.props.editable ? Mode.FILL : Mode.VIEW,
                viewMode: this.props.viewMode || ViewMode.FORM,
                formContext: getDefaultContext(),
                validationFailures: {fields: {}, pages: {}},
                requiredFieldProgressByPage: {},
                validationsInitialized: false
            };
            this.state.language = this.getDefaultLanguage(this.state.formId);

            this.fieldScope = this.props.fieldScope == undefined ? 'form' : this.props.fieldScope;
            this.findComponents = this.findComponents.bind(this);
            this.updateAnswer = this.updateAnswer.bind(this);
            this.deleteRepeatableRegionAnswer = this.deleteRepeatableRegionAnswer.bind(this);
            this.handleViewModeChange = this.handleViewModeChange.bind(this);
            this.handleLanguageChange = this.handleLanguageChange.bind(this);
            this.updateComponentIndex(props, this.state);
            this.hasRunFormReady = false;
            this.getRequiredFieldProgress = this.getRequiredFieldProgress.bind(this);
            this.getRequiredFieldProgressForField = this.getRequiredFieldProgressForField.bind(this);
            this.getInitialValidationFailures = this.getInitialValidationFailures.bind(this);
            this.getValidationFailuresForField = this.getValidationFailuresForField.bind(this);

            this.API = {};

            for (const [key, fn] of Object.entries(apiMethods)) {
              this.API[key] = fn.bind(this); // bind to component instance
            }

            const { validations = [], requiredFields = [] } = this.props.form;
            this.validationsByField = Object.groupBy(validations, ({ repeatedFieldScope, name }) =>
              Helpers.fullFieldName(this.fieldScope, repeatedFieldScope, name),
            );
            this.requiredFieldsByField = Object.groupBy(requiredFields, ({ repeatedFieldScope, name }) =>
              Helpers.fullFieldName(this.fieldScope, repeatedFieldScope, name),
            );

            this._eventListeners = new Map();
        }

        getDefaultLanguage(formId) {
          let defaultLanguage = this.props.language || 'en';
          let availableLanguages = this.props.availableLanguages || [defaultLanguage];
          let currentFormLanguages = this.state.form.languages || [defaultLanguage];

          if (formId) {
            let savedLang = sessionStorage.getItem(`form_language_${formId}`);
            if (savedLang && currentFormLanguages.includes(savedLang)) {
              return savedLang
            }
          }

          return (
            availableLanguages.find(l => currentFormLanguages.includes(l)) || defaultLanguage
          );
        }

        handleLanguageChange(e) {
          let selectedLang = e.target.value;

          this.setState({language: selectedLang});
          if (this.state.formId) {
            sessionStorage.setItem(`form_language_${this.state.formId}`, selectedLang);
          }
        }

        renderLanguageSelector() {
          let formLangOpts = this.state.form.languages;
          let sortedFormLangOpts = (this.props.availableLanguages || []).filter(l => formLangOpts.includes(l));

          if (sortedFormLangOpts.length > 1) {
            return (
              <div className='text-right mt-1'>
                <label>
                  <select className='form-control' onChange={this.handleLanguageChange} defaultValue={this.state.language}>
                    {sortedFormLangOpts.map((l) => (<option key={l} value={l}>{I18n.t(`${l}_long`)}</option>))}
                  </select>
                </label>
              </div>
            )
          }
        }

        render() {
            // TODO: If formContext fails to load, show error as the form can't be correctly filled.
            if (this.state.formContext.loading) {
              return <FormViewerSkeleton />
            }

            return (<PaginationBoundary>
              <FormContext.Provider value={this.state.formContext}>
                <Provider store={store}>
                    {
                      this.props.toggleViewMode && this.props.viewMode == ViewMode.PRETTY &&
                        <div style={{display: 'contents'}}>
                          <label>{I18n.t('pretty_mode')}</label>
                          <div>
                              <ToggleSwitch
                                isOn={this.state.viewMode == ViewMode.PRETTY}
                                defaultIsOn={true}
                                handleToggle={this.handleViewModeChange}
                              />
                          </div>
                          <hr />
                        </div>
                    }

                    {this.renderLanguageSelector()}

                    <div ref="rootNode" className="form-viewer">
                      <PaginationContext.Consumer>
                        {pagination =>
                          <FormContent
                            language={this.state.language}
                            mode={this.state.mode}
                            fieldScope={this.fieldScope}
                            form={this.state.form}
                            answers={this.state.answers}
                            formValidationFailures={this.state.validationFailures}
                            requiredFieldProgressByPage={this.state.requiredFieldProgressByPage}
                            prefillAnswers={this.props.prefillAnswers}
                            lockFormFill={this.props.lockFormFill}
                            prefillAttachments={this.props.prefillAttachments}
                            attachments={this.state.attachments}
                            comments={this.state.comments}
                            attachmentUploadId={this.props.attachmentUploadId}
                            attachmentUploadUrl={this.props.attachmentUploadUrl}
                            attachmentDownloadUrl={this.props.attachmentDownloadUrl}
                            attachmentShowDownloadLink={this.props.attachmentShowDownloadLink}
                            missingAttachments={this.props.missingAttachments}
                            missingAttachmentsSaveUrl={this.props.missingAttachmentsSaveUrl}
                            saveAnswerBlobUrl={this.props.saveAnswerBlobUrl}
                            commentSendUrl={this.props.commentSendUrl}
                            commentEditUrl={this.props.commentEditUrl}
                            commentDeleteUrl={this.props.commentDeleteUrl}
                            renderHeader={this.props.renderHeader}
                            commentToggleThreadUrl={this.props.commentToggleThreadUrl}
                            commentPermissions={this.props.commentPermissions}
                            loggedPersonId={this.props.loggedPersonId}
                            pdf={this.props.pdf}
                            currencyFormat={this.props.currencyFormat}
                            hideSensitiveInformation={this.props.hideSensitiveInformation}
                            showHiddenFields={this.props.showHiddenFields}
                            virusScannerEnabled={this.props.virusScannerEnabled}
                            findComponentsCallback={this.findComponents}
                            updateAnswerCallback={this.updateAnswer}
                            answersChangedCallback={this.answersChanged}
                            triggerEvent={this.triggerEvent}
                            deleteRepeatableRegionAnswerCallback={this.deleteRepeatableRegionAnswer}
                            deleteOptionalRegionAnswerCallback={this.deleteOptionalRegionAnswer}
                            projectId={this.props.projectId}
                            handleDisbursement={this.props.handleDisbursement} 
                            projects={this.props.projects}
                            setDisableSubmit={this.props.setDisableSubmit}
                            groupByColumn={this.props.groupByColumn}
                            viewMode={this.state.viewMode}
                            imagePermissions={this.props.imagePermissions}
                            prefillFormProps={this.props.prefillFormProps}
                            asWebView={this.props.asWebView}
                            optionSetValues={this.props.optionSetValues}
                            optionSetHierarchyValues={this.props.optionSetHierarchyValues}
                            optionSetHierarchies={this.props.optionSetHierarchies}
                            disableEditLocks={this.props.disableEditLocks}
                            pagination={pagination}
                            disablePagedMode={this.props.disablePagedMode}
                          />
                        }
                      </PaginationContext.Consumer>
                    </div>
                </Provider>
              </FormContext.Provider>
            </PaginationBoundary>);
        }

        componentDidMount() {
            this.updateOptionalRegionRequiredFields();

            this.loadContext();
        }

        formReady() {
            this.updateOptionalRegionRequiredFields();
            switch (typeof this.props.ready) {
                case 'function':
                    this.props.ready();
                    break;
                case 'string':
                    // Callback given in string format Module1.ModuleN.function
                    // or just plain function name.
                    let callback = window; // Root for the lookup
                    this.props.ready.split('.').forEach((i) => {
                        callback = callback[i];
                    });
                    if (typeof callback !== 'undefined'){
                        callback();
                    }
                    break;
            }
        }

        componentDidUpdate(prevProps, prevStates) {
            this.updateComponentIndex(this.props, this.state);
            this.updateOptionalRegionRequiredFields();

            const updates = {}

            // Initialize required field counts per page on initial load
            if (typeof this.props.answers !== 'string' && !this.state.validationsInitialized) {
              const validationFailures = this.getInitialValidationFailures(this.props.answers || {});
              const requiredFieldProgressByPage = this.getRequiredFieldProgress(this.props.answers || {});
              updates.requiredFieldProgressByPage = requiredFieldProgressByPage;
              updates.validationFailures = validationFailures;
              updates.validationsInitialized = true;              
            }

            if (prevProps.answers !== this.props.answers) {
              updates.answers = this.props.answers;
            }

            if (Object.keys(updates).length > 0) {
              this.setState(updates);
            }

            if (prevStates.formContext.loading === true &&
                this.state.formContext.loading === false &&
                !this.hasRunFormReady){
                this.formReady();
                this.hasRunFormReady = true;  
            }
        }

        loadContext() { 
          // In Fill mode, load all connected presets, otherwise just the definitions to load selection options
          loadDatagridContext(
            {
              programmeFormLinkId: this.props.programmeFormLinkId,
              programmeFormId: this.props.programmeFormId || this.props.formId,
              projectId: this.state.formContext.projectId,
              publicContext: this.props.publicContext
            },
            {
              loadPresetValues: this.state.mode == FormComponent.Mode.FILL
            }
          ).then(context => {
            this.setState((prevState) => {
              const newFormContext = {...prevState.formContext, ...context};
              let answers = prevState.answers;
              if (!_.isEmpty(newFormContext.datagridRows)) {
                answers = resolveDatagridDerivedAnswers(answers, newFormContext, prevState.form, this.fieldScope, false);
              }
              return { formContext: newFormContext, answers };
            });
          })
        }

        handleViewModeChange = () => {
            let viewMode = this.state.viewMode == ViewMode.FORM ? ViewMode.PRETTY : ViewMode.FORM;
            this.setState({viewMode: viewMode, mode: viewMode == ViewMode.FORM ? Mode.FILL : Mode.VIEW});
        }

        triggerEvent = (eventName, eventParams) => {
          const handlers = this._eventListeners.get(eventName);

          if (!handlers) return;

          // Possibly issues if new listeners are added.
          for (const handler of [...handlers]) {
            handler(eventParams);
          }
        }

        // Update per-page required counts based on individual field
        getRequiredFieldProgressForField(answers, fieldName, requiredFieldProgressByPage = {}) {
          const requiredFieldName = fieldName.replace(/\[row-\d+\]/g, '[row-0]');
          const requiredFields = this.requiredFieldsByField[requiredFieldName] || [];
          const fieldNamePath = fieldName.split(/[\[\]]+/).filter(part => part && part !== this.fieldScope);

          // TBD: Annoying jQuery selector use, but these hidden inputs currently indicate optional region presence to the backend
          // Required field inside optional region
          let optionalRequired = !!jQuery(this.refs.rootNode).find(
            'input[name="' + this.fieldScope + '[optional_region_required_fields][]"][value="' + fieldNamePath[fieldNamePath.length - 1] + '"]'
          ).length;

          // Required field inside repeatable region that is inside optional regionrequired
          const rowMatch = fieldName.match(/\[row-(\d+)\]/);
          if (!optionalRequired && rowMatch) {
            optionalRequired = !!jQuery(this.refs.rootNode).find(
              'input[name="' + this.fieldScope + '[repeatable_optional_region_required_fields][' + fieldNamePath[fieldNamePath.length - 1] + '][]"][value="' + rowMatch[1] + '"]'
            ).length;
          }

          requiredFields.forEach(({ pageId, isWithinOptionalRegion }) => {
            if (pageId == null || (isWithinOptionalRegion && !optionalRequired)) {
              return;
            }

            const answer = Helpers.getObjectValue(answers, fieldNamePath);
            const answered = answer != null && answer.length !== 0;

            const progress = requiredFieldProgressByPage[pageId] ??= { total: 0, answered: 0, fields: {} };

            const previous = progress.fields[fieldName];

            if (previous == null) {
              progress.total += 1;
            } else if (previous) {
              progress.answered -= 1;
            }

            if (answered) {
              progress.answered += 1;
            }

            progress.fields[fieldName] = answered;
          });

          return requiredFieldProgressByPage;
        }

        // Resolve field scopes, expanding repeatable regions to their current rows.
        getFieldScopes(repeatedFieldScope, answers) {
          // Non-repeatable regions have no scope
          if (!repeatedFieldScope?.includes('[row-0]')) {
            return [repeatedFieldScope];
          }

          const repeatedFieldName = repeatedFieldScope.split('[row-0]')[0];
          const repeatedAnswers = answers[repeatedFieldName] || {};
          const rows = Object.keys(repeatedAnswers).filter(key => /^row-\d+$/.test(key));

          // Always include row-0. May not exist in answers even when it does in UI, if empty.
          const allRows = ['row-0', ...rows.filter(row => row !== 'row-0')];
          return allRows.map(row => repeatedFieldScope.replace('[row-0]', `[${row}]`));
        }

        // Go through all required fields in form for initial per-page display
        getRequiredFieldProgress(answers) {
          const { requiredFields = [] } = this.props.form;
          const requiredFieldProgressByPage = {};

          requiredFields.forEach(({ repeatedFieldScope, name, label, pageId }) => {
            if (pageId == null) return;

            this.getFieldScopes(repeatedFieldScope, answers).forEach(scope => {
              const fieldName = Helpers.fullFieldName(this.fieldScope, scope, name);
              this.getRequiredFieldProgressForField(answers, fieldName, requiredFieldProgressByPage);
            });
          });

          return requiredFieldProgressByPage;
        }

        // Initial load of all validation errors (old submissions that were saved with invalid inputs)
        getInitialValidationFailures(answers) {
          const { validations = [] } = this.props.form;
          const validationFailures = { fields: {}, pages: {} };

          validations.forEach(({ repeatedFieldScope, name }) => {
            this.getFieldScopes(repeatedFieldScope, answers).forEach(scope => {
              const fieldName = Helpers.fullFieldName(this.fieldScope, scope, name);
              const failures = this.getValidationFailuresForField(fieldName, answers);

              validationFailures.fields[fieldName] = failures
                .filter(({ type }) => type === ValidationType.INVALID)
                .map(({ label }) => label);

              failures.forEach(({ pageId, type, label }) => {
                if (pageId == null || type !== ValidationType.INVALID) return;

                validationFailures.pages[pageId] ??= {};
                validationFailures.pages[pageId][fieldName] ??= [];
                validationFailures.pages[pageId][fieldName].push(label);
              });
            });
          });

          return validationFailures;
        }

        getValidationFailuresForField(fieldName, answers) {
          const fieldNamePath = fieldName.split(/[\[\]]+/).filter(part => part && part !== this.fieldScope);

          const answer = Helpers.getObjectValue(answers, fieldNamePath);

          if (this.state.mode !== Mode.FILL || answer == null) {
            return [];
          }

          // Normalize row-n to row-0 to match saved validation scopes.
          const validationFieldName = fieldName.replace(/\[row-\d+\]/g, '[row-0]');

          const fallbackFieldName = fieldNamePath[fieldNamePath.length - 1] 
            ? Helpers.fullFieldName(this.fieldScope, null, fieldNamePath[fieldNamePath.length - 1])
            : '##UNDEFINED';

          // Legacy form-level validations/requirements for components in repeatable regions may lack
          // repeatedFieldScope until the form is saved again.
          const validationList = this.validationsByField[validationFieldName] || this.validationsByField[fallbackFieldName] || []
          const requiredFields = this.requiredFieldsByField[validationFieldName] || this.requiredFieldsByField[fallbackFieldName] || [];

          const failures = [];
          let requiredFailureAdded = false;

          validationList.forEach(({ name, label, dataType, validations, pageId }) => {
            const param = label[this.state.language] || name;

            if (requiredFields.length > 0 && (answer == null || answer.length === 0) && !requiredFailureAdded) {
              failures.push({type: ValidationType.REQUIRED, label: I18n.t('error_param_presence', {param}), pageId: pageId})
              requiredFailureAdded = true;
            }

            const convertedValue = Helpers.convertValue(answer, dataType);

            if (validations != null && answer.length !== 0) {
              if (validations['min'] && convertedValue < (validations['dataType'] != 'date' ? validations['min'] : Date.parse(validations['min']))) {
                failures.push({type: ValidationType.INVALID, label: I18n.t('error_param_min', {param, value: validations['min']}), pageId: pageId})
              }

              if (validations['max'] && convertedValue > (validations['dataType'] != 'date' ? validations['max'] : Date.parse(validations['max']))) {
                failures.push({type: ValidationType.INVALID, label: I18n.t('error_param_max', {param, value: validations['max']}), pageId: pageId})
              }

              const formatMismatch =
                validations['dataType'] == FormComponent.DataType.STRING &&
                ((validations['format'] == FormComponent.Format.EMAIL && !convertedValue.match(FormComponent.FormatRegExp.EMAIL)) ||
                  (validations['format'] == FormComponent.Format.URL && !convertedValue.match(FormComponent.Format.URL)));

              if (formatMismatch) {
                failures.push({type: ValidationType.INVALID, label: I18n.t('error_param_format', { param }), pageId: pageId});
              }

              if (validations['dataType'] == 'integer' && !/^[-]?[0-9]*$/.test(String(answer ?? '').trim())){
                failures.push({type: ValidationType.INVALID, label: I18n.t('error_param_integer', {param}), pageId: pageId})
              }

              if (validations['dataType'] == 'decimal' && !/^[-]?[0-9 ]*(\.|,)?[0-9]+$/.test(String(answer ?? '').trim())){
                failures.push({type: ValidationType.INVALID, label: I18n.t('error_param_decimal', {param}), pageId: pageId})
              }

              if (validations['dataType'] == 'date' && (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(String(answer ?? '').trim())) || convertedValue == null){
                failures.push({type: ValidationType.INVALID, label: I18n.t('error_param_date', {param}), pageId: pageId})
              }
            }
          });

          return failures;
        }

        updateAnswer(fieldName, answer) {
          // DEBUG: Who called console.log((new Error()).stack?.split("\n")[2]?.trim().split(" ")[1])
            this.setState(prevState => {
                let prevAnswers = prevState.answers || {};
                let validationFailures = prevState.validationFailures || {};
                let fieldNamePath = fieldName.split(/[\[\]]+/).filter((s) => (['', this.fieldScope].indexOf(s) == -1));

                // Use the immutability helper to only update the objects that are needed
                let updateObj = {}
                let ptr = updateObj;
                fieldNamePath.forEach((key, idx) => {
                  ptr[key] =  (idx == fieldNamePath.length -1) ? {$set: answer} : {}
                  ptr = ptr[key];
                })

                const isRepeatableRegionRow = fieldNamePath[fieldNamePath.length - 1]?.startsWith('row-');

                // Immutability helper doesn't create nested structures. Use setObjectValue first to ensure strucutre exists
                // Must use a value that is not the same as the answer, since it will not be notived by react otherwise
                Helpers.setObjectValue(prevAnswers, fieldNamePath, '##UNDEFINED');
                let answers = update(prevAnswers, updateObj);

                // Capture validation failures validated on form level.
                // Index failed validations at both page level and field name level, former only used for paged forms
                const failures = this.getValidationFailuresForField(fieldName, answers);
                validationFailures.fields[fieldName] = failures.map(({ label }) => label);

                Object.values(validationFailures.pages).forEach((pageFailures) => {
                  pageFailures[fieldName] = [];
                });

                failures.forEach(({ pageId, type, label }) => {
                  // Old requiredFields arrays on forms don't have pageId, but such shouldn't need
                  // page-level required field counts in any case
                  if (pageId == null || type !== ValidationType.INVALID) return;

                  validationFailures.pages[pageId] ??= {};
                  validationFailures.pages[pageId][fieldName] ??= [];
                  validationFailures.pages[pageId][fieldName].push(label);
                });

                if (!_.isEmpty(prevState.formContext.datagridRows)) {
                  // Skip preset updates so that changes are not automatically reverted on answer updates
                  answers = resolveDatagridDerivedAnswers(answers, prevState.formContext, prevState.form, this.fieldScope, true);
                }

                // When adding repeatable region rows, have to check for all as child keys don't exist yet
                const requiredFieldProgressByPage = isRepeatableRegionRow
                  ? this.getRequiredFieldProgress(answers)
                  : this.getRequiredFieldProgressForField(answers, fieldName, { ...this.state.requiredFieldProgressByPage });

                return { answers, validationFailures, requiredFieldProgressByPage };
            });
        }

        answersChanged = () => {
          if (typeof this.props.valueChanged == 'function') { 
            this.props.valueChanged(this.state.answers);
          }
        }

        /**
         * Finds components based on provided search conditions.
         *
         * @param {Object} conditions - Criteria used to search for components.
         * @param {string|string[]} [conditions.dataType] - One or more data types to match components against.
         * @param {string} [conditions.id] - Component ID to search for.
         * @param {string} [conditions.name] - Component field_name to search for.
         * @param {string} [conditions.className] - Component class name to search for.
         * @param {string} [conditions.type] - Component type to search for.
         * @returns {Object[]} Array of matched component objects.
         */
        findComponents(conditions) {
            if (typeof conditions.dataType != 'undefined') {
                if (typeof conditions.dataType == "string") {
                    return this.componentIndex.dataType[conditions.dataType] || [];
                } else {
                    let components = [];
                    conditions.dataType.forEach((t) => {
                        components = [...components, ...(this.componentIndex.dataType[t] || [])]
                    });
                    return components;
                }
            }

            if (typeof conditions.id != 'undefined') {
                return this.componentIndex.id[conditions.id] ? [this.componentIndex.id[conditions.id]] : [];
            }

            if (typeof conditions.name != 'undefined') {
                return this.componentIndex.name[conditions.name] ? [this.componentIndex.name[conditions.name]] : [];
            }

            if (typeof conditions.type != 'undefined') {
                return this.componentIndex.type[conditions.type] || [];
            }

            let components = [];

            if (typeof conditions.className != 'undefined') {
                components = components.concat(this.componentIndex.className[conditions.className] || []);
            }
            return components;
        }

        updateComponentIndex(nextProps, nextState) {
          this.componentIndex = { id: {}, className: {}, name: {}, dataType: {}, type: {} };
          if (nextState.form.hasOwnProperty('pages')) {
            nextState.form.pages[0].components.forEach((comp) => {
                [comp].concat(Resolver.componentClass(comp.type).nestedComponents(comp)).forEach((c) => {
                    this.componentIndex.id[c.id] = c;

                    if (c.name) {
                        this.componentIndex.name[c.name] = c;
                    }

                    if (c.dataType) {
                        this.componentIndex.dataType[c.dataType] = this.componentIndex.dataType[c.dataType] || [];
                        this.componentIndex.dataType[c.dataType].push(c);
                    }

                    if (c.type) {
                        this.componentIndex.type[c.type] = this.componentIndex.type[c.type] || [];
                        this.componentIndex.type[c.type].push(c);
                    }

                    Resolver.componentClassNames().forEach((className) => {
                        if (Resolver.componentClassName(c.type) == className || (Resolver.componentClass(c.type).TYPE && Resolver.componentClass(c.type).prototype instanceof FormComponent[className])) {
                            this.componentIndex.className[className] = this.componentIndex.className[className] || [];
                            this.componentIndex.className[className].push(c);
                        }
                    });
                });
            });
          }
        }

        updateOptionalRegionRequiredFields() {
            let formRoot = jQuery(this.refs.rootNode);

            const optionalSelector = 'input[type="hidden"].optional-region-required-field';
            const repeatableSelector = 'input[type="hidden"].repeatable-region-required-field';

            const beforeOptionalCount = formRoot.find(optionalSelector).length;
            const beforeRepeatableCount = formRoot.find(repeatableSelector).length;

            formRoot.find('input[type="hidden"].optional-region-required-field').remove();
            formRoot.find('input[type="hidden"].repeatable-region-required-field').remove();

            (this.state.form.requiredFields || []).forEach((r) => {
                let collections = formRoot.find('[name*="[' + r.name + ']"]')
                if (r.isWithinOptionalRegion && collections.length > 0) {
                    let fieldScope = this.fieldScope
                    formRoot.append('<input type="hidden" name="' + fieldScope + '[optional_region_required_fields][]" value="' + r.name + '" class="optional-region-required-field" />');
                    collections.each(function(i){
                        let name = collections[i].name || ''
                        if(name.split('row-').length > 1){
                            let index = name.split('row-')[1].split('][' + r.name)[0]
                            formRoot.append('<input type="hidden" name="' + fieldScope + '[repeatable_optional_region_required_fields][' + r.name + '][]" value="' + index + '" class="repeatable-region-required-field" />')
                        }
                    })
                }
            });

            const afterOptionalCount = formRoot.find(optionalSelector).length;
            const afterRepeatableCount = formRoot.find(repeatableSelector).length;

            const countsChanged = beforeOptionalCount !== afterOptionalCount || beforeRepeatableCount !== afterRepeatableCount;
            const repeatableRegionAdded = beforeOptionalCount === afterOptionalCount && afterRepeatableCount > beforeRepeatableCount;

            // When adding repeatable regions, getRequiredFieldProgress is called by updateAnswer.
            if (countsChanged && !repeatableRegionAdded) {
              this.setState({ requiredFieldProgressByPage: this.getRequiredFieldProgress(this.state.answers) });
            }
        }

        deleteRepeatableRegionAnswer(fieldName, rowIndex) {
            let region = this.findComponents({ name: fieldName })[0];
            let answers = JSON.parse(JSON.stringify(this.state.answers));
            let attachments = JSON.parse(JSON.stringify(this.state.attachments));
            let regionAnswers = {}

            for (let rowId in answers[fieldName] || {}) {
                let idx = parseInt(rowId.split('-')[1], 10);

                if (idx < rowIndex) {
                    regionAnswers[rowId] = answers[fieldName][rowId];
                } else if (idx == rowIndex) {
                    // The answer is deleted by not including it in regionAnswers anymore.
                    // Additionally delete possible attachments within the region.
                    if (answers[fieldName][rowId]) {
                        FormComponent.RepeatableRegion.nestedComponents(region, {type: FormComponent.Attachment.TYPE}).forEach((a) => {
                            let attachmentAttrs = [];
                            for (let attr in attachments || {}) {
                                if ([answers[fieldName][rowId][a.name] || []].flat().includes(attr)) {
                                    attachmentAttrs.push(attr);
                                }
                            }
                            attachmentAttrs.forEach(attr => FormComponent.Attachment.markDeletedFile(attr, this.props.fieldScope, jQuery(this.refs.rootNode).closest("form")));
                        });
                    }
                } else if (idx > rowIndex) {
                    regionAnswers['row-' + (idx - 1)] = answers[fieldName][rowId];
                }
            }
            answers[fieldName] = regionAnswers;

            const requiredFieldProgressByPage = this.getRequiredFieldProgress(answers);

            this.setState({answers: answers, attachments: attachments, requiredFieldProgressByPage: requiredFieldProgressByPage});
        }

        deleteOptionalRegionAnswer = (id) => {
            let region = this.findComponents({ id })[0];
            let answers = { ...this.state.answers };
            let attachments = { ...this.state.attachments };

            FormComponent.OptionalRegion.nestedComponents(region).forEach((c) => {
                if (c.type == FormComponent.Attachment.TYPE || c.type == FormComponent.EvaluationAttachment.TYPE) {
                    let attachmentAttrs = [];
                    for (let attr in attachments || {}) {
                        if ([answers[c.name] || []].flat().includes(attr)) {
                            attachmentAttrs.push(attr);
                        }
                    }
                    attachmentAttrs.forEach((attr) => FormComponent.Attachment.markDeletedFile(attr, this.props.fieldScope, jQuery(this.refs.rootNode).closest("form")));
                } else if (c.type == FormComponent.RepeatableRegion.TYPE) {
                    // This is done the delete each attachment inside Repeatable region
                    Object.keys(answers[c.name] || {}).forEach((rowId, idx) => this.deleteRepeatableRegionAnswer(c.name, idx));
                }

                delete answers[c.name];
            });

            this.setState({ answers, attachments });
        }
}



/*
  Returns the props to be provided to a component based on component type.
*/
export const resolveComponentProps = (component, props, additionalProps = {}) => {
  const ComponentClass = Resolver.componentClass(component.type);

  // Props used by components that contain question / other answer dependent fields
  const answerComponentProps = ComponentClass?.ANSWER_DEPENDENT ? {
    answers: props.answers,
    attachments: props.attachments,
  } : NO_ANSWERS

  // Defaul props shared for all compontents / in all views where components are created
  // Can be overwritten by providing additionalProps
  const commonProps = {
    language: props.language,
    key: component.id,
    content: component,
    fieldScope: props.fieldScope,
    triggerEvent: props.triggerEvent,
    selectCallback: props.selectComponentCallback,
    selectedId: props.selectedFormComponentId,
    mode: props.mode,
    attachmentUploadId: props.attachmentUploadId,
    attachmentUploadUrl: props.attachmentUploadUrl,
    attachmentDownloadUrl: props.attachmentDownloadUrl,
    attachmentShowDownloadLink: props.attachmentShowDownloadLink,
    missingAttachments: props.missingAttachments,
    missingAttachmentsSaveUrl: props.missingAttachmentsSaveUrl,
    saveAnswerBlobUrl: props.saveAnswerBlobUrl,
    commentSendUrl: props.commentSendUrl,
    commentEditUrl: props.commentEditUrl,
    commentDeleteUrl: props.commentDeleteUrl,
    commentToggleThreadUrl: props.commentToggleThreadUrl,
    commentPermissions: props.commentPermissions,
    loggedPersonId: props.loggedPersonId,
    pdf: props.pdf,
    currencyFormat: props.currencyFormat,
    hideSensitiveInformation: props.hideSensitiveInformation,
    showHiddenFields: props.showHiddenFields,
    virusScannerEnabled: props.virusScannerEnabled,
    startDragCallback: props.startDragCallback,
    findComponentsCallback: props.findComponentsCallback,
    deleteComponentCallback: props.deleteComponentCallback,
    replaceComponentContent: props.replaceComponentContent,
    updateAnswerCallback: props.updateAnswerCallback,
    answersChangedCallback: props.answersChangedCallback,
    deleteRepeatableRegionAnswerCallback: props.deleteRepeatableRegionAnswerCallback,
    deleteOptionalRegionAnswerCallback: props.deleteOptionalRegionAnswerCallback,
    projectId: props.projectId,
    prefillAnswers: props.prefillAnswers,
    comments: props.comments,
    prefillAttachments: props.prefillAttachments,
    indicatorLevel: props.indicatorLevel || (props.content || NO_ANSWERS).indicatorLevel,
    handleDisbursement: props.handleDisbursement,
    projects: props.projects,
    project: props.project,
    readOnly: props.readOnly,
    lockFormFill: props.lockFormFill,
    setDisableSubmit: props.setDisableSubmit,
    groupByColumn: props.groupByColumn,
    viewMode: props.viewMode,
    safeMode: props.safeMode,
    imagePermissions: props.imagePermissions,
    prefillFormProps: props.prefillFormProps || NO_ANSWERS,
    asWebView: props.asWebView,
    optionSetValues: props.optionSetValues,
    optionSetHierarchyValues: props.optionSetHierarchyValues,
    optionSetHierarchies: props.optionSetHierarchies,
    disableEditLocks: props.disableEditLocks,
    autoComplete: props.autoComplete,
    formValidationFailures: props.formValidationFailures,
    requiredFieldProgressByPage: props.requiredFieldProgressByPage,
    pagination: props.pagination,
    disablePagedMode: props.disablePagedMode
  };

  return {
    ...commonProps,
    ...answerComponentProps,
    ...additionalProps
  }

}
